import React, { useState, useEffect, useRef } from "react";
import cx from "classnames";
import Search from "./searchIcons/IcMapSearch";
import { point as turfPoint, buffer, bbox as turfBbox, bboxPolygon } from "@turf/turf";
import { SearchItemIcon } from "./SearchBoxIcon";
import { SearchOptions, fuzzySearch } from "./SearchApi";
import { useOnClickOutside } from "../../utils/useOnClickOutside";
import { useDebounce } from "../../utils/useDebounce";
import "./SearchBox.css";
import IcMapCancel from "./searchIcons/IcMapCancel";
import Highlighted from "./Highlighted";

const mod = (a: number, b: number) => ((a % b) + b) % b;

const parseCoords = (str: string) => {
  let coords;
  try {
    coords = str.split(",").map(x => parseFloat(x.trim()));
    coords.forEach(x => {
      if (isNaN(x)) {
        throw new Error("");
      }
    });
    if (coords.length !== 2) {
      throw new Error("");
    }
  } catch (e) {
    return undefined;
  }
  return coords;
};

const coordinatesToSearchItem = ([lat, lng]: number[]): SearchItem => {
  const point = turfPoint([lng, lat]);

  const buffered = buffer(point, 0.5, { units: "kilometers" });
  const bbox = turfBbox(buffered);
  const bboxPolygonRes = bboxPolygon(bbox);

  const bboxCoords = bboxPolygonRes.geometry.coordinates[0];
  const topLeftPoint = { lon: bboxCoords[3][0], lat: bboxCoords[3][1] };
  const btmRightPoint = { lon: bboxCoords[1][0], lat: bboxCoords[1][1] };

  return {
    id: `${lat},${lng}`,
    lat,
    lng,
    label: `${lat}, ${lng}`,
    bbox: {
      btmRightPoint,
      topLeftPoint
    }
  };
};

const searchResultToSearchItem = (item: any): SearchItem => {
  const { freeformAddress, country } = item.address;
  const splitedAddress = freeformAddress.split(",");
  const label = item.poi ? item.poi.name : splitedAddress[0];
  const description = item.poi
    ? `${freeformAddress}, ${country}`
    : splitedAddress[1]
    ? `${splitedAddress[1]}, ${country}`
    : `${country}`;
  return {
    id: item.id,
    label,
    description,
    lat: item.position.lat,
    lng: item.position.lon,
    bbox: item.viewport,
    originalItem: item
  };
};

interface Props {
  onSearch: (e: SearchItem) => void;
  onClear?: () => void;
  searchOptions: SearchOptions;
  emptyState?: any;
  highlightSuggestions?: Boolean;
  placeholder?: string;
  isAutoFocusActive?: boolean;
}

export interface SearchItem {
  id: string;
  label: string;
  description?: string;
  lat: number;
  lng: number;
  bbox?: any;
  /** Original search result */
  originalItem?: any;
}

function SearchBox(props: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchItem[]>([]);
  const [resultsQuery, setResultsQuery] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [selectedResult, setSelectedResult] = useState(0);
  const inputRef = useRef<HTMLInputElement>();
  const listRef = useRef();

  useOnClickOutside(inputRef, (e: any) => {
    if (listRef.current && !(listRef.current as any).contains(e.target)) {
      setFocused(false);
    }
  });

  const handleSearch = (item: SearchItem) => {
    if (loading) {
      return;
    }
    if (inputRef.current !== undefined) {
      inputRef.current.blur();
    }
    setFocused(false);
    setQuery(item.label + (item.description ? ", " + item.description : ""));
    props.onSearch(item);
  };

  const debouncedQuery = useDebounce(query, 150);
  const hasPressedEnter = useRef(false);

  useEffect(() => {
    if(props.isAutoFocusActive){
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [props.isAutoFocusActive]);

  useEffect(() => {
    let active = true;
    const request = async () => {
      if (debouncedQuery.trim().length === 0) {
        setResults(undefined);
        return;
      }
      setLoading(true);
      const results: SearchItem[] = [];
      const coords = parseCoords(debouncedQuery);
      if (coords) {
        results.push(coordinatesToSearchItem(coords));
      }
      const defaultOptions = { typeahead: true, limit: 20 };
      const searchResult = await fuzzySearch(debouncedQuery, {
        ...defaultOptions,
        ...props.searchOptions
      });
      if (!active) {
        return;
      }
      results.push(...searchResult.results.map(searchResultToSearchItem));
      if (hasPressedEnter.current && results && results.length > 0) {
        handleSearch(results[0]);
      }
      hasPressedEnter.current = false;

      setLoading(false);
      setResults(results);
      setResultsQuery(debouncedQuery);
      setSelectedResult(0);
    };
    request();
    return () => {
      active = false;
    };
  }, [debouncedQuery, JSON.stringify(props.searchOptions)]);

  useEffect(() => {
    if (debouncedQuery === "") {
      if (props.onClear) {
        props.onClear();
      }
    }
  }, [debouncedQuery]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedResult(i => mod(i + 1, results.length));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedResult(i => mod(i - 1, results.length));
    }
    if (e.key === "Escape") {
      (e.target as any).blur();
      setFocused(false);
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const staleResults = query !== resultsQuery;
      if (results && results.length > 0 && !staleResults) {
        handleSearch(results[selectedResult]);
      } else {
        hasPressedEnter.current = true;
      }
    }
  };

  return (
    <div className="SearchBox">
      <div className="SearchBox__input-wrapper">
        <div className="SearchBox__input-icon">
          <Search />
        </div>
        <input
          className="SearchBox__input"
          ref={inputRef}
          placeholder={props.placeholder ?? "Search"}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={() => setFocused(true)}
          onFocus={() => setFocused(true)}
        />
        {query.length > 0 && (
          <div className="SearchBox__input-cancel" onClick={() => setQuery("")}>
            <IcMapCancel />
          </div>
        )}
      </div>
      {results && focused && (
        <ul
          ref={listRef}
          className={cx("SearchBox__list", {
            "SearchBox__list--pending": loading
          })}
        >
          {results.length === 0 && <div className="SearchBox__empty">{props.emptyState}</div>}
          {results.map((item: SearchItem, i: number) => (
            <li
              key={item.id}
              onClick={() => handleSearch(item)}
              className={cx("SearchBox__list-item", {
                "SearchBox__list-item--selected": selectedResult === i && focused
              })}
            >
              <div className="SearchBox__item-icon">
                <SearchItemIcon
                  classifications={
                    item.originalItem?.poi?.classifications ?? [
                      { code: item.originalItem?.type === "Cross Street" ? "JUNCTION" : "FALLBACK" }
                    ]
                  }
                />
              </div>
              <div className="SearchBox__item-info">
                {props.highlightSuggestions ? (
                  <Highlighted
                    text={item.label}
                    highlight={query}
                    className={"SearchBox__item-label"}
                    highlightColor={"#61ade"}
                  />
                ) : (
                  <div className="SearchBox__item-label">{item.label}</div>
                )}

                {item.description && (
                  <div className="SearchBox__item-description">{item.description}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
export default SearchBox;
